import {
    Button,
    DataTable,
    Heading,
    Labelled,
    Stack,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import { CircleCancelMajorMonotone } from "@shopify/polaris-icons";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import useTimeout from "../../hooks/useTimeout";
import { ComponentLabelPolaris } from "../shared/ComponentLabelPolaris";

export default function AmazonIdeaDropdownOption({
    optionsList,
    onChange,
    disabled,
}) {
    const [optionValues, setOptionValues] = useState(
        optionsList?.length > 0 ? optionsList : [],
    );

    const [setDelay] = useTimeout();
    const handleOptionChange = useCallback(
        (index) => (value, id) => {
            const option = (optionValues || []).find((_, i) => i === index);
            if (option) {
                option[id] = value;
                optionValues[index] = option;

                setOptionValues([...optionValues]);

                setDelay(() => {
                    onChange(optionValues);
                }, 1000);
            }
        },
        [optionValues, onChange],
    );

    const handleAddOption = useCallback(() => {
        const len = optionValues.length;
        const newOption = {
            name: `Option ${len + 1}`,
            priceDifference: "",
        };

        const newOptions = [...optionValues, newOption];
        setOptionValues(newOptions);
        setDelay(() => {
            onChange(newOptions);
        }, 1000);
    }, [optionValues, onChange]);

    const handleDelete = useCallback(
        (index) => () => {
            optionValues.splice(index, 1);
            setOptionValues([...optionValues]);
            setDelay(() => {
                onChange(optionValues);
            }, 1000);
        },
        [optionValues, onChange],
    );

    const rows = (optionValues || []).map((option, index) => {
        return [
            <div className="option-name">
                <TextField
                    id="name"
                    value={option.name}
                    onChange={handleOptionChange(index)}
                    placeholder={`Option ${index}`}
                    disabled={disabled}
                />
            </div>,
            <div className="option-price">
                <TextField
                    id="priceDifference"
                    value={option.priceDifference + ""}
                    onChange={handleOptionChange(index)}
                    placeholder={`0.0`}
                    prefix={"$"}
                    type="number"
                    step="0.01"
                    disabled={disabled}
                />
            </div>,
            <div className="option-delete">
                <Tooltip content="Delete">
                    <Button
                        icon={CircleCancelMajorMonotone}
                        plain
                        onClick={handleDelete(index)}
                        disabled={disabled}
                    />
                </Tooltip>
            </div>,
        ];
    });

    return (
        <Wrapper>
            <Stack vertical>
                <Stack vertical spacing="extraTight">
                    <Labelled>
                        <Heading element="h3">Options List</Heading>
                    </Labelled>
                    <DataTable
                        rows={rows}
                        verticalAlign="middle"
                        headings={[
                            <ComponentLabelPolaris
                                required
                                label="Option Name"
                            />,
                            "Price difference",
                            "Delete",
                        ]}
                        columnContentTypes={["text", "text", "numeric"]}
                        hideScrollIndicator
                    />
                </Stack>

                {disabled ? null : (
                    <div>
                        <Button
                            children="Add Option"
                            onClick={handleAddOption}
                            plain
                        />
                    </div>
                )}
            </Stack>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    margin-top: 1.6rem;

    .Polaris-DataTable__Table thead th {
        padding-top: 0;
    }

    .Polaris-DataTable__Cell {
        padding-left: 3px;
    }
`;
